<script lang="ts" setup>
import { PropType, ref, computed } from "vue";
import { type DocumentSpace } from "@/graphql/operations";
import { useI18n } from "vue-i18n";
import { Dropdown, Tooltip, UpgradeModal } from "@/components";
import { Formatter } from "@/util";
import { useStore } from "vuex";
import CopySpaceModal from "../CopySpaceModal.vue";
import DeleteSpaceModal from "../DeleteSpaceModal.vue";
import { useSpaceModalDisplay } from "@/composables";

const {
  displayCopyDocumentSpaceModal,
  displayUpgradeModal,
  copyDocumentSpaceModalVisible,
  upgradeModalVisible,
  handleModalDisplay,
} = useSpaceModalDisplay();

const emits = defineEmits(["reloadData"]);

const { t } = useI18n({});
const store = useStore();

const props = defineProps({
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
});

const contextMenuItems = computed(() => {
  const i: Array<{
    value: string;
    title: string;
    destructive?: boolean;
  }> = [{ value: "copy", title: t("documentSpaces.copy") }];

  const myAccess = props.documentSpace.documentSpaceAccesses.nodes.find(
    (a) => a?.user?.id === store.getters.user.postgresId,
  );

  if (myAccess && myAccess.role === "owner") {
    i.push({
      value: "remove",
      title: t("documentSpaces.delete"),
      destructive: true,
    });
  }

  return i;
});

const displayConfirmation = ref<boolean>(false);
const handleAction = async (action: string) => {
  if (action === "remove") {
    displayConfirmation.value = true;
  } else {
    await handleModalDisplay("copy");
  }
};

const deleteSpace = async () => {
  displayConfirmation.value = false;
  emits("reloadData");
};

const isActive = computed(
  () => props.documentSpace.documentSpaceVouchers?.totalCount >= 1,
);

const isVisited = computed(() =>
  Boolean(props.documentSpace.visitStatistics?.lastVisitAt),
);
</script>

<template>
  <tr :data-test="`shared-space-${documentSpace.id}`">
    <td>
      <router-link
        :to="{
          name: 'DocumentSpace',
          params: { id: documentSpace.id },
        }"
      >
        {{ documentSpace.name }}

        <span v-if="store.getters.numSpaceConfigs > 1" class="badge">
          {{ documentSpace.config?.name }}
        </span>
        <Tooltip v-if="isActive && !isVisited">
          <template v-slot>
            <span class="material-symbols-outlined success"> check</span>
          </template>
          <template #tooltip>
            {{ $t("documentSpaces.activated") }}
          </template>
        </Tooltip>
        <Tooltip v-else-if="isActive && isVisited">
          <template v-slot>
            <span class="material-symbols-outlined success"> done_all</span>
          </template>
          <template #tooltip>
            {{ $t("documentSpaces.activatedAndVisited") }}
          </template>
        </Tooltip>
        <Tooltip v-if="documentSpace.documentSpaceAccesses.nodes.length > 1">
          <template v-slot>
            <span class="material-symbols-outlined"> group </span>
          </template>
          <template #tooltip>
            {{ $t("documentSpaces.sharedDoc") }}
          </template>
        </Tooltip>
      </router-link>
    </td>
    <td>
      <router-link
        :to="{
          name: 'DocumentSpace',
          params: { id: documentSpace.id },
        }"
      >
        <span v-if="documentSpace.visitStatistics?.lastVisitAt">
          {{
            Formatter.formatAndTranslateDate(
              documentSpace.visitStatistics.lastVisitAt,
            )
          }}
        </span>
      </router-link>
    </td>
    <td>
      <router-link
        :to="{
          name: 'DocumentSpace',
          params: { id: documentSpace.id },
        }"
      >
        {{ Formatter.formatAndTranslateDate(documentSpace.updatedAt) }}
      </router-link>
    </td>
    <td>
      <router-link
        :to="{
          name: 'DocumentSpace',
          params: { id: documentSpace.id },
        }"
      >
        {{
          $t(
            "documentSpaces.numDocuments",
            documentSpace.documentSpaceItems?.totalCount || 0,
          )
        }}
      </router-link>
    </td>
    <td>
      <Dropdown class="right" :items="contextMenuItems" @change="handleAction">
        <span class="material-symbols-outlined">more_vert</span>
      </Dropdown>
    </td>
  </tr>
  <delete-space-modal
    v-if="displayConfirmation"
    :active="true"
    :space-id="documentSpace.id"
    :name="documentSpace.name || ''"
    @close="displayConfirmation = false"
    @success="deleteSpace"
  />

  <upgrade-modal
    v-if="upgradeModalVisible"
    :inherit-classic-theme="true"
    @close="displayUpgradeModal = false"
  />

  <CopySpaceModal
    v-if="copyDocumentSpaceModalVisible"
    :inherit-classic-theme="true"
    @close="displayCopyDocumentSpaceModal = false"
    :sourceSpaceId="documentSpace.id"
    :sourceSpaceName="documentSpace.name || ''"
  />
</template>

<style scoped lang="scss">
table a {
  text-decoration: none;
  width: 100%;
  color: inherit;
  font-weight: normal;
}

table thead tr th:last-child,
table tbody tr td:last-child {
  width: 30px;
}

table td .material-symbols-outlined {
  font-size: 1.3em;
  border-radius: 50%;
  padding: 0.2em;
}

table td .material-symbols-outlined {
  font-size: 1.3em;
}

.material-symbols-outlined {
  vertical-align: middle;
}

.material-symbols-outlined.success {
  color: var(--classic-success, $color-success);
}

.tooltip_container {
  margin-left: 0.5em;
}

td a {
  display: inline-block;
  min-height: 20px;
}

@media (prefers-color-scheme: dark) {
  td a:focus-visible {
    outline: 2px solid var(--classic-focus);
    outline-offset: 2px;
  }
}

.badge {
  align-items: center;
  border-radius: 4px;
  padding: 2px 6px;
  background: var(--classic-accent-soft, #d6fafb);
  font-size: 0.7em;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 0.5em;
}
</style>
